import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = ["href"]
const _hoisted_3 = ["src"]
const _hoisted_4 = { key: 1 }
const _hoisted_5 = {
  key: 2,
  class: "submitted-consent ion-padding"
}
const _hoisted_6 = { class: "signature-preview ion-margin-top" }
const _hoisted_7 = ["src"]
const _hoisted_8 = { class: "consent-text" }
const _hoisted_9 = { class: "signature-container" }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { class: "button-container" }

import { defineComponent, ref, reactive, onMounted, watch, computed, onBeforeUnmount } from 'vue';

// icons
import { arrowBack, checkmarkCircle, } from 'ionicons/icons';

// components
import { IonPage, IonGrid, IonHeader, IonToolbar, IonTitle, IonContent, IonButton, IonButtons, IonLabel, IonIcon,
          IonInput, modalController } from '@ionic/vue';

// services
import AchieveJUPASService from '@/services/AchieveJUPASService';
import CommonService from '@/services/CommonService';

// lib / composables
import SignaturePad from 'signature_pad';
import { utils } from '@/composables/utils';
import { useStore } from '@/store';
import { useRoute, useRouter } from 'vue-router';

// types
import { User } from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'ParentConsentSignatureModal',
  props: ['isPage', 'target', 'videoId', 'targetEvent'],
  setup(__props) {

// vue
const props = __props;
const router = useRouter();
const route = useRoute();
const { presentToast, presentPrompt, uniqueId, formatDate, closeModal, } = utils();
const goBack = async () => {
  if (props.isPage) router.replace('/home');
  else await modalController.dismiss({});
};
const consentTarget = route.params.target || props.target || 'achievejupas_parent_consent';
const consentVideoId = route.params.videoId || props.videoId;

// store
const store = useStore();
const user = computed<User>(() => store.state.user);
const targetVideo = ref<any>(null);
const formData = reactive({
  childFullName: '',
  childPhoneNumber: '',
});

// signature pad
const signaturePadElement = ref<HTMLCanvasElement | null>(null);
const signaturePad = ref<SignaturePad | null>(null);
const hasSignature = ref(false);

const initSignaturePad = () => {
  const canvas: any = document.querySelector("#signaturePad");

  signaturePad.value = new SignaturePad(canvas, {
    backgroundColor: 'rgb(255, 255, 255)',
    penColor: 'rgb(0, 0, 0)',
  });
  
  signaturePad.value.addEventListener('endStroke', () => {
    hasSignature.value = !signaturePad.value?.isEmpty();
  });
};

const resizeCanvas = () => {
  const canvas: any = document.querySelector("#signaturePad");
  const ratio = Math.max(window.devicePixelRatio || 1, 1);
  canvas.width = canvas.offsetWidth * ratio;
  canvas.height = canvas.offsetHeight * ratio;
  canvas.getContext('2d')?.scale(ratio, ratio);
  if (signaturePad.value) signaturePad.value.clear();
};

onMounted(() => {
  initSignaturePad();
  setTimeout(() => {
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
  }, 300);

  if (consentVideoId) {
    CommonService.getVideoById(consentVideoId).then((res) => {
      console.log(res);
      targetVideo.value = res;
    });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeCanvas);
  if (signaturePad.value) {
    signaturePad.value.off();
  }
});

const clearSignature = () => {
  if (signaturePad.value) {
    signaturePad.value.clear();
    hasSignature.value = false;
  }
};

const responseByTarget = computed(() => {
  switch (consentTarget) {
    case 'video':
      return 'I hereby consent the video involving my child/ward to be published.';
    case 'bluebird-seed':
      return 'I hereby consent to my child/ward joining Bluebird Seed.';
    case 'shooting':
      return 'I hereby consent to my child/ward joining the video shooting session.';
    default:
      return 'I hereby consent to my child/ward using AchieveJUPAS.';
  }
});

const validateChildInfo = () => {
  const { childFullName, childPhoneNumber } = formData;
  if (!user.value?.id) {
    if (!childFullName?.trim()) {
      presentToast('Please enter child\'s full name', 2000);
      return false;
    }
    if (!childPhoneNumber?.trim()) {
      presentToast('Please enter child\'s phone number', 2000);
      return false;
    }
  }
  return true;
};

const saveSignature = async () => {
  if (signaturePad.value?.isEmpty()) {
    presentToast('Please provide your signature', 2000);
    return;
  }
  
  if (!validateChildInfo()) return;
  
  presentPrompt("Submit your consent with signature?", async () => {
    try {
      const signatureDataUrl = signaturePad.value?.toDataURL("image/jpeg", 0.5);
      const sessionInfo = props.targetEvent ? 
        `${props.targetEvent.name} (${props.targetEvent.formattedDateTime})` : "";
      
      const { schoolId, fullName, roles, phone, waGroupId } = user.value;
      const isLoggedIn = !!user.value?.id;
      const userConsentRecordId = `uc${uniqueId()}`;
      
      const payload = {
        id: userConsentRecordId,
        schoolId,
        fullName: isLoggedIn ? fullName : formData.childFullName.trim(),
        target: consentTarget,
        videoId: consentVideoId,
        videoLink: targetVideo.value?.link,
        roles: isLoggedIn ? roles : 'anonymous',
        response: responseByTarget.value,
        phone: isLoggedIn ? phone : formData.childPhoneNumber.trim(),
        waGroupId,
        signatureDataUrl,
        createdAt: new Date(),
        sessionId: props.targetEvent?.id,
        sessionInfo,
      };
      AchieveJUPASService.insertUserConsent(payload);
      user.value.userConsentRecords?.push(payload);
      presentToast('Parent consent recorded successfully', 2000);

      // Reset the form
      formData.childPhoneNumber = '';
      formData.childFullName = '';
      clearSignature();

      // Close modal
      closeModal({ userConsentRecordId });
    } catch (error) {
      console.error('Error saving parent consent:', error);
        presentToast('Failed to record parent consent. Please try again', 2000);
    }
  });
};

const submittedConsent = computed(() => {
  return user.value.userConsentRecords?.find(r => {
    return r.target == consentTarget && (!consentVideoId || r.videoId == consentVideoId) && (!props.targetEvent || props.targetEvent?.id == r.sessionId)
  });
});

const getEmbeddedYTLink = (link: string) => {
  return link?.replace('watch?v=', 'embed/') || '';
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonPage), null, {
    default: _withCtx(() => [
      _createVNode(_unref(IonHeader), null, {
        default: _withCtx(() => [
          _createVNode(_unref(IonGrid), { fixed: "" }, {
            default: _withCtx(() => [
              _createVNode(_unref(IonToolbar), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(IonButtons), { slot: "start" }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(IonButton), {
                        slot: "icon-only",
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (goBack()))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(IonIcon), { icon: _unref(arrowBack) }, null, 8, ["icon"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(IonTitle), null, {
                    default: _withCtx(() => [
                      (submittedConsent.value)
                        ? (_openBlock(), _createBlock(_unref(IonLabel), { key: 0 }, {
                            default: _withCtx(() => _cache[3] || (_cache[3] = [
                              _createTextVNode("Parent Consent Obtained")
                            ])),
                            _: 1,
                            __: [3]
                          }))
                        : (_openBlock(), _createBlock(_unref(IonLabel), { key: 1 }, {
                            default: _withCtx(() => _cache[4] || (_cache[4] = [
                              _createTextVNode("Parent/Guardian Consent")
                            ])),
                            _: 1,
                            __: [4]
                          }))
                    ]),
                    _: 1
                  }),
                  (submittedConsent.value)
                    ? (_openBlock(), _createBlock(_unref(IonButtons), {
                        key: 0,
                        slot: "end"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(IonButton), { slot: "icon-only" }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(IonIcon), {
                                slot: "end",
                                color: "success",
                                icon: _unref(checkmarkCircle)
                              }, null, 8, ["icon"])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_unref(IonContent), {
        class: "ion-padding",
        style: {"--padding-bottom":"200px"}
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(IonGrid), { fixed: "" }, {
            default: _withCtx(() => [
              (_unref(consentVideoId) && targetVideo.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                    _createElementVNode("a", {
                      href: targetVideo.value?.link,
                      target: "_blank"
                    }, _toDisplayString(targetVideo.value?.title || 'Video'), 9, _hoisted_2),
                    _createElementVNode("iframe", {
                      style: {"width":"100%","height":"380px"},
                      src: getEmbeddedYTLink(targetVideo.value?.link),
                      frameborder: "0",
                      allowfullscreen: ""
                    }, null, 8, _hoisted_3)
                  ]))
                : _createCommentVNode("", true),
              (__props.targetEvent)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createElementVNode("b", null, _toDisplayString(__props.targetEvent.name), 1),
                    _cache[5] || (_cache[5] = _createElementVNode("br", null, null, -1)),
                    _cache[6] || (_cache[6] = _createTextVNode(" on ")),
                    _createElementVNode("b", null, _toDisplayString(__props.targetEvent.formattedDateTime), 1)
                  ]))
                : _createCommentVNode("", true),
              (submittedConsent.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _createElementVNode("p", null, " Parent/guardian consent was submitted on " + _toDisplayString(_unref(formatDate)(submittedConsent.value.createdAt)), 1),
                    _createElementVNode("div", _hoisted_6, [
                      _cache[7] || (_cache[7] = _createElementVNode("p", { class: "signature-label" }, "Parent/Guardian Signature:", -1)),
                      _createElementVNode("img", {
                        src: submittedConsent.value.signatureImg || submittedConsent.value.signatureDataUrl,
                        alt: "Parent's Signature",
                        class: "signature-image"
                      }, null, 8, _hoisted_7)
                    ])
                  ]))
                : _createCommentVNode("", true),
              _withDirectives(_createElementVNode("div", null, [
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("p", null, _toDisplayString(responseByTarget.value), 1)
                ]),
                _createElementVNode("div", _hoisted_9, [
                  (!user.value?.id)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                        _createVNode(_unref(IonInput), {
                          "label-placement": "stacked",
                          fill: "solid",
                          label: "Child's Full Name*",
                          modelValue: formData.childFullName,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.childFullName) = $event)),
                          type: "text",
                          placeholder: "Enter child's full name",
                          required: ""
                        }, null, 8, ["modelValue"]),
                        _createVNode(_unref(IonInput), {
                          "label-placement": "stacked",
                          fill: "solid",
                          label: "Child's Phone Number*",
                          modelValue: formData.childPhoneNumber,
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.childPhoneNumber) = $event)),
                          type: "text",
                          placeholder: "Enter child's phone number",
                          required: ""
                        }, null, 8, ["modelValue"])
                      ]))
                    : _createCommentVNode("", true),
                  _cache[8] || (_cache[8] = _createElementVNode("p", { class: "signature-label" }, "Parent/Guardian Signature:", -1)),
                  _cache[9] || (_cache[9] = _createElementVNode("canvas", {
                    id: "signaturePad",
                    class: "signature-pad"
                  }, null, -1)),
                  _cache[10] || (_cache[10] = _createElementVNode("p", null, [
                    _createTextVNode("By signing in the box, you agree to our Terms and Conditions ("),
                    _createElementVNode("a", {
                      href: "https://www.fdmt.hk/legal",
                      target: "_blank"
                    }, "fdmt.hk/legal"),
                    _createTextVNode(")")
                  ], -1))
                ]),
                _createElementVNode("div", _hoisted_11, [
                  _createVNode(_unref(IonButton), {
                    color: "danger",
                    fill: "outline",
                    onClick: clearSignature,
                    disabled: !hasSignature.value
                  }, {
                    default: _withCtx(() => _cache[11] || (_cache[11] = [
                      _createTextVNode("Clear")
                    ])),
                    _: 1,
                    __: [11]
                  }, 8, ["disabled"]),
                  _createVNode(_unref(IonButton), {
                    color: "success",
                    onClick: saveSignature,
                    disabled: !hasSignature.value
                  }, {
                    default: _withCtx(() => _cache[12] || (_cache[12] = [
                      _createTextVNode("Submit")
                    ])),
                    _: 1,
                    __: [12]
                  }, 8, ["disabled"])
                ])
              ], 512), [
                [_vShow, !submittedConsent.value]
              ])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})